/**
 * The organization chart (GET /api/v1/org-chart). The chart is
 * docs/org/org-chart.json verbatim; `roster` is the server's cross-check
 * against the employees table. No resident or patient data travels here.
 */
import { apiRequest } from './api';

export type Tier = 'ELT' | 'SLT' | 'MANAGER' | 'STAFF' | 'VACANT';

export interface OrgWorkspace { workspace: string; as: string; basis: string; inferred: boolean }

export interface OrgPosition {
  id: string;
  name: string | null;
  chartTitle: string | null;
  rosterTitle: string | null;
  tier: Tier;
  reportsTo: string | null;
  email: string | null;
  emailLooksShared?: boolean;
  vacant: boolean;
  areas: string[];
  workspaces: OrgWorkspace[];
  notes: string;
  confirm: string[];
}

export interface OrgContact { position: string; as: string; inferred: boolean }

export interface OrgRoute {
  id: string;
  topic: string;
  examples?: string[];
  primary: OrgContact[];
  backup: OrgContact[];
  basis: string;
}

export interface OrgQuestion {
  id: string;
  askWho: string;
  cc: string[];
  topic: string;
  question: string;
  options?: string[];
  defaultIfNoAnswer?: string;
}

export interface OrgChart {
  schemaVersion: number;
  title: string;
  source: { transcribedOn: string; rosterReadOn: string; authority: string; [k: string]: string };
  tiers: Record<Tier, { label: string; chartColour: string; meaning: string }>;
  assumptions: { id: string; text: string; confirmWith: string[] }[];
  positions: OrgPosition[];
  routing: OrgRoute[];
  ambiguities: { id: string; text: string; question: string }[];
  openQuestions: OrgQuestion[];
}

export interface RosterFlag {
  inRoster: boolean;
  rosterActive: boolean | null;
  emailMatches: boolean | null;
  matchedBy: 'email' | 'name' | null;
  rosterTitle: string | null;
}

export interface OrgRoster {
  available: boolean;
  positions: Record<string, RosterFlag>;
  notOnChart: { name: string; email: string | null; jobTitle: string | null; active: boolean }[];
}

export interface OrgChartResponse { chart: OrgChart; roster: OrgRoster }

export interface OrgChartResult {
  success: boolean;
  data?: OrgChartResponse;
  status?: number;
  code?: string;
  message?: string;
}

export const orgChartApi = {
  get: (): Promise<OrgChartResult> => apiRequest<OrgChartResponse>('GET', '/org-chart') as Promise<OrgChartResult>,
};
